import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { crmDataService } from './utils/crmDataService';
import ApprovalTimeline from './components/ApprovalTimeline';
import './styles/crmDashboard.css';

function TicketDetail() {
  const { ticketNumber } = useParams();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [history, setHistory] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({
    status: '',
    priority: '',
    assignedTo: ''
  });

  const loadComments = useCallback(async (ticketNumber) => {
    try {
      if (!ticketNumber) return;
      const data = await crmDataService.getTicketComments(ticketNumber);
      setComments(data || []);
    } catch (err) {
      console.error('Failed to load comments:', err);
    }
  }, []);

  const loadFieldValues = useCallback(async (ticketId) => {
    try {
      const data = await crmDataService.getTicketFieldValues(ticketId);
      setFieldValues(data || {});
    } catch (err) {
      console.error('Failed to load field values:', err);
    }
  }, []);

  const loadHistory = useCallback(async (ticketNumber) => {
    try {
      if (!ticketNumber) return;
      const data = await crmDataService.getTicketHistory(ticketNumber);
      setHistory(data || []);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  }, []);

  const loadTeamMembers = useCallback(async (teamCode) => {
    try {
      if (!teamCode) return;
      const data = await crmDataService.getTeamMembers(teamCode);
      setTeamMembers(data || []);
    } catch (err) {
      console.error('Failed to load team members:', err);
    }
  }, []);

  const loadTicketData = useCallback(async () => {
    if (!ticketNumber) return;

    try {
      setLoading(true);
      const ticketData = await crmDataService.getTicketByNumber(ticketNumber);
      setTicket(ticketData);

      // Set edit data defaults
      setEditData({
        status: ticketData.status || '',
        priority: ticketData.priority || '',
        assignedTo: ticketData.assigned_to || ''
      });

      // Load additional data
      // Note: comments and history use ticket_number, fieldValues uses id
      await Promise.all([
        loadComments(ticketData.ticket_number),
        loadFieldValues(ticketData.id),
        loadHistory(ticketData.ticket_number),
        loadTeamMembers(ticketData.team_code)
      ]);

      setError(null);
    } catch (err) {
      console.error('Failed to load ticket:', err);
      setError('Failed to load ticket: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [ticketNumber, loadComments, loadFieldValues, loadHistory, loadTeamMembers]);

  useEffect(() => {
    loadTicketData();
  }, [loadTicketData]);

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      setCommentLoading(true);
      const comment = await crmDataService.addTicketComment(ticket.id, {
        comment: newComment,
        isInternal: false
      });

      setComments([comment, ...comments]);
      setNewComment('');
      setSuccessMessage('Comment added successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to add comment: ' + err.message);
    } finally {
      setCommentLoading(false);
    }
  };

  const handleUpdateTicket = async (e) => {
    e.preventDefault();

    try {
      const updated = await crmDataService.updateTicket(ticket.id, editData);
      setTicket(updated);
      setShowEditModal(false);
      setSuccessMessage('Ticket updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);

      // Reload history to show the update
      await loadHistory(ticket.id);
    } catch (err) {
      setError('Failed to update ticket: ' + err.message);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': '#4A90E2',
      'open': '#50C878',
      'pending': '#F5A623',
      'resolved': '#7B68EE',
      'closed': '#6C757D'
    };
    return colors[status?.toLowerCase()] || '#6C757D';
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      'low': 'priority-low',
      'medium': 'priority-medium',
      'high': 'priority-high',
      'urgent': 'priority-urgent'
    };
    return badges[priority?.toLowerCase()] || 'priority-medium';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  if (loading) {
    return (
      <div className="crm-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading ticket...</p>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="crm-dashboard-container">
        <div className="alert alert-error">
          Ticket not found
        </div>
        <button className="btn-primary" onClick={() => navigate('/hr/crm/tickets')}>
          Back to Tickets
        </button>
      </div>
    );
  }

  const requiresApproval = ticket.team_id === 1 && ticket.category_id === 25;

  return (
    <div className="crm-dashboard-container ticket-detail">
      <div className="crm-dashboard-header">
        <div>
          <button className="btn-back" onClick={() => navigate('/hr/crm/tickets')}>
            ← Back
          </button>
          <h1>Ticket {ticket.ticket_number}</h1>
          <p className="subtitle">{ticket.title}</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => setShowEditModal(true)}>
            Edit Ticket
          </button>
        </div>
      </div>

      {requiresApproval && ticket.approval_flow && ticket.approval_flow.length > 0 && (
        <div className="detail-approval-timeline">
          <ApprovalTimeline flow={ticket.approval_flow} ticketStatus={ticket.status} />
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <span>{successMessage}</span>
        </div>
      )}

      <div className="ticket-detail-content">
        {/* Left Column - Main Info */}
        <div className="ticket-main">
          {/* Ticket Info Card */}
          <div className="ticket-info-card">
            <div className="ticket-header-row">
              <div className="ticket-meta">
                <span
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(ticket.status) }}
                >
                  {ticket.status}
                </span>
                <span className={`badge ${getPriorityBadge(ticket.priority)}`}>
                  {ticket.priority} priority
                </span>
              </div>
              <div className="ticket-dates">
                <small>Created {formatDate(ticket.created_at)}</small>
                {ticket.updated_at && (
                  <small>Updated {formatRelativeTime(ticket.updated_at)}</small>
                )}
              </div>
            </div>

            <div className="ticket-description">
              <h3>Description</h3>
              <p>{ticket.description}</p>
            </div>

            {/* Conditional Fields */}
            {Object.keys(fieldValues).length > 0 && (
              <div className="ticket-fields">
                <h3>Additional Information</h3>
                <div className="field-values-grid">
                  {Object.entries(fieldValues).map(([key, fieldData]) => {
                    // Handle different value types - fieldData may be {value, name, type} or a simple value
                    let displayLabel = key;
                    let displayValue;

                    if (fieldData === null || fieldData === undefined) {
                      displayValue = 'N/A';
                    } else if (typeof fieldData === 'object' && !Array.isArray(fieldData)) {
                      // Object with {value, name, type} structure from backend
                      displayLabel = fieldData.name || key;
                      displayValue = fieldData.value ?? 'N/A';
                    } else if (Array.isArray(fieldData)) {
                      displayValue = fieldData.map(v => typeof v === 'object' ? (v.value || v.name || JSON.stringify(v)) : v).join(', ');
                    } else {
                      displayValue = fieldData;
                    }

                    return (
                      <div key={key} className="field-value-item">
                        <label>{displayLabel}</label>
                        <span>{String(displayValue)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Comments Section */}
          <div className="ticket-comments-section">
            <h3>Comments & Activity</h3>

            <form onSubmit={handleAddComment} className="add-comment-form">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                rows="4"
                required
              />
              <div className="comment-actions">
                <button
                  type="submit"
                  disabled={commentLoading || !newComment.trim()}
                  className="btn-primary"
                >
                  {commentLoading ? 'Adding...' : 'Add Comment'}
                </button>
              </div>
            </form>

            <div className="comments-list">
              {comments.length === 0 ? (
                <p className="no-comments">No comments yet</p>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="comment-item">
                    <div className="comment-header">
                      <strong>{comment.author_name || 'Unknown'}</strong>
                      <span className="comment-time">{formatRelativeTime(comment.created_at)}</span>
                    </div>
                    <div className="comment-body">{comment.comment}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* History Section */}
          {history.length > 0 && (
            <div className="ticket-history-section">
              <h3>History</h3>
              <div className="history-timeline">
                {history.map((event, index) => (
                  <div key={index} className="history-event">
                    <div className="event-time">{formatRelativeTime(event.created_at)}</div>
                    <div className="event-description">{event.description}</div>
                    <div className="event-author">{event.author_name}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Sidebar */}
        <div className="ticket-sidebar">
          <div className="sidebar-card">
            <h3>Details</h3>
            <div className="detail-row">
              <label>Team</label>
              <span>{ticket.team_name || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <label>Category</label>
              <span>{ticket.category_name || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <label>Subcategory</label>
              <span>{ticket.subcategory_name || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <label>Store</label>
              <span>{ticket.store_pc || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <label>Assigned To</label>
              <span>{ticket.assigned_to_name || 'Unassigned'}</span>
            </div>
            <div className="detail-row">
              <label>Requester</label>
              <div>
                <div>{ticket.requester_name}</div>
                <small>{ticket.requester_email}</small>
              </div>
            </div>
          </div>

          {ticket.sla_due_date && (
            <div className="sidebar-card sla-card">
              <h3>SLA</h3>
              <div className="detail-row">
                <label>Due Date</label>
                <span>{formatDate(ticket.sla_due_date)}</span>
              </div>
              {ticket.sla_status && (
                <div className="sla-status">
                  <span className={`sla-badge sla-${ticket.sla_status}`}>
                    {ticket.sla_status}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Ticket</h2>
              <button onClick={() => setShowEditModal(false)}>×</button>
            </div>

            <form onSubmit={handleUpdateTicket}>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={editData.status}
                  onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                  required
                >
                  <option value="new">New</option>
                  <option value="open">Open</option>
                  <option value="pending">Pending</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div className="form-group">
                <label>Priority</label>
                <select
                  value={editData.priority}
                  onChange={(e) => setEditData({ ...editData, priority: e.target.value })}
                  required
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div className="form-group">
                <label>Assign To</label>
                <select
                  value={editData.assignedTo}
                  onChange={(e) => setEditData({ ...editData, assignedTo: e.target.value })}
                >
                  <option value="">Unassigned</option>
                  {teamMembers.map(member => (
                    <option key={member.id} value={member.id}>
                      {member.name} ({member.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TicketDetail;